import { api } from '@/api/endpoints';
import { clearTokens, persistTokens } from '@/api/client';
import { StorageKeys } from '@/lib/config';
import type { DriverProfile } from '@/types/domain';
import { createLogger } from '@/lib/logger';

const log = createLogger('auth');

const ACTIVE_VALUES = new Set(['active', 'a', 'enabled', 'yes', 'true', '1']);

/**
 * Service that owns "is the driver logged in?" state. Wraps the API
 * client by also enforcing the `drv_status === Active` gate after
 * a successful credential check.
 */
export const authService = {
  async login(driverNumber: string, password: string): Promise<DriverProfile> {
    const result = await api.login(driverNumber.trim(), password);
    const status = (result.driver?.status ?? '').trim().toLowerCase();
    // If the middleware returns a status field, enforce Active. If it
    // omits the field we trust the middleware (which already checks
    // drv_status server-side -- see W15 RESOLVED).
    if (status && !ACTIVE_VALUES.has(status)) {
      log.warn('login blocked: driver status not active', status);
      clearTokens();
      throw new Error('Driver account is not active. Contact dispatch.');
    }

    persistTokens({
      accessToken: result.accessToken,
      refreshToken: result.refreshToken,
      expiresAt: result.expiresAt,
    });
    localStorage.setItem(StorageKeys.driverNumber, result.driver.driverNumber);
    return result.driver;
  },

  logout(): void {
    clearTokens();
    localStorage.removeItem(StorageKeys.driverNumber);
    localStorage.removeItem(StorageKeys.lastSync);
  },

  isLoggedIn(): boolean {
    return Boolean(localStorage.getItem(StorageKeys.accessToken));
  },

  getDriverNumber(): string | null {
    return localStorage.getItem(StorageKeys.driverNumber);
  },
};
